/**
 * Client-side fuzzy search utility for full-text search
 * Supports fuzzy matching, partial word matching, and relevance scoring
 */

/**
 * Normalizes text for searching by:
 * - Converting to lowercase
 * - Removing extra whitespace
 * - Removing common punctuation
 */
function normalizeText(text: string): string {
  if (!text) return '';
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s]/g, ' ') // Replace punctuation with spaces
    .replace(/\s+/g, ' '); // Collapse multiple spaces
}

/**
 * Calculates a simple similarity score between two strings
 * Returns a value between 0 and 1, where 1 is an exact match
 */
function calculateSimilarity(str1: string, str2: string): number {
  const s1 = normalizeText(str1);
  const s2 = normalizeText(str2);
  
  // Exact match
  if (s1 === s2) return 1.0;
  
  // One contains the other
  if (s1.includes(s2) || s2.includes(s1)) return 0.9;
  
  // Calculate word-based similarity
  const words1 = s1.split(/\s+/).filter(w => w.length > 0);
  const words2 = s2.split(/\s+/).filter(w => w.length > 0);
  
  if (words1.length === 0 || words2.length === 0) return 0;
  
  // Check if all query words appear in the text (in any order)
  const allWordsMatch = words2.every(word2 => 
    words1.some(word1 => word1.includes(word2) || word2.includes(word1))
  );
  
  if (allWordsMatch) {
    // Calculate how many words match exactly vs partially
    let exactMatches = 0;
    let partialMatches = 0;
    
    words2.forEach(word2 => {
      const exactMatch = words1.some(word1 => word1 === word2);
      const partialMatch = words1.some(word1 => 
        word1.includes(word2) || word2.includes(word1)
      );
      
      if (exactMatch) exactMatches++;
      else if (partialMatch) partialMatches++;
    });
    
    // Score based on exact vs partial matches
    const totalWords = words2.length;
    return 0.5 + (exactMatches / totalWords) * 0.3 + (partialMatches / totalWords) * 0.2;
  }
  
  // Check for character-level similarity (simple approach)
  let matchingChars = 0;
  const minLength = Math.min(s1.length, s2.length);
  const maxLength = Math.max(s1.length, s2.length);
  
  for (let i = 0; i < minLength; i++) {
    if (s1[i] === s2[i]) matchingChars++;
  }
  
  // Also check for substring matches
  let substringMatch = 0;
  for (let i = 0; i <= s1.length - 3; i++) {
    const substr = s1.substring(i, i + 3);
    if (s2.includes(substr)) substringMatch++;
  }
  
  const charSimilarity = matchingChars / maxLength;
  const substringSimilarity = substringMatch / Math.max(1, s1.length - 2);
  
  return Math.max(charSimilarity * 0.3, substringSimilarity * 0.4);
}

/**
 * Checks if a query matches text using fuzzy search
 * Returns a relevance score (0-1) or null if no match
 */
export function fuzzyMatch(query: string, text: string | null | undefined): number | null {
  if (!query || !text) return null;
  
  const normalizedQuery = normalizeText(query);
  const normalizedText = normalizeText(text);
  
  if (normalizedQuery.length === 0) return null;
  
  // Split query into individual words
  const queryWords = normalizedQuery.split(/\s+/).filter(w => w.length > 0);
  
  // Check if all query words appear in the text
  const allWordsFound = queryWords.every(queryWord => {
    // Check for exact word match
    if (normalizedText.includes(queryWord)) return true;
    
    // Check for partial word match (at least 3 characters)
    if (queryWord.length >= 3) {
      return normalizedText.includes(queryWord.substring(0, 3));
    }
    
    // For very short words, check character by character
    return queryWord.split('').every(char => normalizedText.includes(char));
  });
  
  if (!allWordsFound) {
    // Try similarity-based matching for typos
    const similarity = calculateSimilarity(normalizedQuery, normalizedText);
    if (similarity >= 0.3) {
      return similarity;
    }
    return null;
  }
  
  // Calculate relevance score
  let score = 0.5; // Base score for matching
  
  // Boost score for exact phrase match
  if (normalizedText.includes(normalizedQuery)) {
    score = 1.0;
  }
  // Boost score if query words appear in order
  else if (queryWords.length > 1) {
    const textWords = normalizedText.split(/\s+/);
    let wordsInOrder = 0;
    let textIndex = 0;
    
    for (const queryWord of queryWords) {
      while (textIndex < textWords.length) {
        if (textWords[textIndex].includes(queryWord)) {
          wordsInOrder++;
          textIndex++;
          break;
        }
        textIndex++;
      }
    }
    
    if (wordsInOrder === queryWords.length) {
      score = 0.9;
    } else {
      score = 0.6 + (wordsInOrder / queryWords.length) * 0.3;
    }
  }
  // Boost score for word start matches
  else {
    const queryWord = queryWords[0];
    const textWords = normalizedText.split(/\s+/);
    const startsWithMatch = textWords.some(word => word.startsWith(queryWord));
    if (startsWithMatch) {
      score = 0.8;
    }
  }
  
  return score;
}

/**
 * Search interface for items with multiple searchable fields
 */
export interface SearchableItem {
  [key: string]: any;
}

/**
 * Search configuration for different fields with weights
 */
export interface SearchConfig {
  fields: Array<{
    name: string;
    weight: number; // Higher weight = more important
  }>;
}

/**
 * Performs fuzzy search on an array of items
 * Returns items sorted by relevance score
 */
export function fuzzySearch<T extends SearchableItem>(
  items: T[],
  query: string,
  config: SearchConfig
): T[] {
  if (!query || query.trim().length === 0) {
    return items;
  }
  
  const results: Array<{ item: T; score: number }> = [];
  
  for (const item of items) {
    let totalScore = 0;
    let totalWeight = 0;
    let hasMatch = false;
    
    for (const field of config.fields) {
      const fieldValue = item[field.name];
      if (fieldValue === null || fieldValue === undefined) continue;
      
      // Handle arrays (e.g., tags, categories)
      if (Array.isArray(fieldValue)) {
        for (const arrayItem of fieldValue) {
          const matchScore = fuzzyMatch(query, String(arrayItem));
          if (matchScore !== null) {
            totalScore += matchScore * field.weight;
            totalWeight += field.weight;
            hasMatch = true;
          }
        }
      } else {
        const matchScore = fuzzyMatch(query, String(fieldValue));
        if (matchScore !== null) {
          totalScore += matchScore * field.weight;
          totalWeight += field.weight;
          hasMatch = true;
        }
      }
    }
    
    if (hasMatch) {
      const finalScore = totalWeight > 0 ? totalScore / totalWeight : 0;
      results.push({ item, score: finalScore });
    }
  }
  
  // Sort by score (highest first)
  results.sort((a, b) => b.score - a.score);
  
  return results.map(r => r.item);
}

/**
 * Convenience function for searching services
 * Optimized for service search with name, description, price, and add-on status
 */
export function searchServices<
  T extends { name: string; description?: string; price: number | string | null | undefined }
>(
  services: T[],
  query: string
): T[] {
  if (!query || query.trim().length === 0) {
    return services;
  }
  
  const normalizedQuery = normalizeText(query);
  
  // Check if query is related to add-ons
  const addOnKeywords = ['addon', 'add on', 'add-on', 'addons', 'add ons', 'add-ons'];
  const isAddOnSearch = addOnKeywords.some(keyword => 
    normalizedQuery.includes(keyword) || 
    normalizedQuery === 'add' ||
    (normalizedQuery.startsWith('add') && normalizedQuery.length <= 6)
  );
  
  // Try to parse as a number for price search
  const priceMatch = normalizedQuery.match(/[\d.]+/);
  const searchPrice = priceMatch ? parseFloat(priceMatch[0]) : null;
  
  const results: Array<{ service: T; score: number }> = [];
  
  for (const service of services) {
    let score = 0;
    let hasMatch = false;
    
    // Check if service is an add-on (check both isAddOn and is_add_on)
    const isAddOn = (service as any).isAddOn || (service as any).is_add_on || false;
    
    // Search add-on status
    if (isAddOnSearch) {
      if (isAddOn) {
        score += 2.5; // High weight for add-on status match
        hasMatch = true;
      } else {
        // If searching for add-ons but this isn't one, skip other searches
        // (unless it matches other fields, which we'll check below)
      }
    }
    
    // Search name (highest weight)
    const nameScore = fuzzyMatch(query, service.name);
    if (nameScore !== null) {
      score += nameScore * 3;
      hasMatch = true;
    }
    
    // Search description (medium weight)
    if (service.description) {
      const descScore = fuzzyMatch(query, service.description);
      if (descScore !== null) {
        score += descScore * 2;
        hasMatch = true;
      }
    }
    
    // Search price (if query contains numbers)
    if (searchPrice !== null) {
      const rawPrice = (service as any)?.price;
      const priceValue =
        typeof rawPrice === 'number'
          ? rawPrice
          : typeof rawPrice === 'string'
            ? parseFloat(rawPrice || '0')
            : 0;
      const priceStr = priceValue.toString();
      const formattedPrice = priceValue.toFixed(2);
      if (priceStr.includes(searchPrice.toString()) || 
          formattedPrice.includes(searchPrice.toString())) {
        score += 1.5;
        hasMatch = true;
      }
    }
    
    // Search category name if available
    const category = (service as any).category;
    if (category) {
      const categoryName = category.name || category.category_name || '';
      const categoryScore = fuzzyMatch(query, categoryName);
      if (categoryScore !== null) {
        score += categoryScore * 1.5;
        hasMatch = true;
      }
    }
    
    // If searching for add-ons and this service is not an add-on, 
    // only include it if it matches other fields (name, description, etc.)
    if (isAddOnSearch && !isAddOn && !hasMatch) {
      continue; // Skip non-add-on services when specifically searching for add-ons
    }
    
    if (hasMatch) {
      // Normalize score (divide by max possible weight)
      // Max weight is now 3+2.5+2+1.5+1.5 = 10.5
      const normalizedScore = score / 10.5;
      results.push({ service, score: normalizedScore });
    }
  }
  
  // Sort by score (highest first)
  results.sort((a, b) => b.score - a.score);
  
  return results.map(r => r.service);
}

/**
 * Convenience function for searching customers/users
 * Optimized for customer search with email, name (first/last), and phone number
 */
export function searchCustomers<T extends {
  email: string;
  firstName?: string;
  lastName?: string | null;
  phoneNumber?: string | null;
}>(customers: T[], query: string): T[] {
  if (!query || query.trim().length === 0) {
    return customers;
  }
  
  const normalizedQuery = normalizeText(query);
  
  // Check if query looks like a phone number (contains mostly digits)
  const phoneDigits = query.replace(/\D/g, '');
  const isPhoneSearch = phoneDigits.length >= 3;
  
  // Check if query looks like an email (contains @)
  const isEmailSearch = query.includes('@');
  
  const results: Array<{ customer: T; score: number }> = [];
  
  for (const customer of customers) {
    let score = 0;
    let hasMatch = false;
    
    // Search email (highest weight, especially if query contains @)
    const emailScore = fuzzyMatch(query, customer.email);
    if (emailScore !== null) {
      // Boost score if query contains @ (more likely to be email search)
      score += emailScore * (isEmailSearch ? 4 : 3);
      hasMatch = true;
    }
    
    // Search first name (high weight)
    if (customer.firstName) {
      const firstNameScore = fuzzyMatch(query, customer.firstName);
      if (firstNameScore !== null) {
        score += firstNameScore * 3;
        hasMatch = true;
      }
    }
    
    // Search last name (high weight)
    if (customer.lastName) {
      const lastNameScore = fuzzyMatch(query, customer.lastName);
      if (lastNameScore !== null) {
        score += lastNameScore * 3;
        hasMatch = true;
      }
    }
    
    // Search full name (first + last combined) - extra weight for full name matches
    const fullName = `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
    if (fullName) {
      const fullNameScore = fuzzyMatch(query, fullName);
      if (fullNameScore !== null) {
        score += fullNameScore * 3.5; // Slightly higher weight for full name
        hasMatch = true;
      }
    }
    
    // Search phone number (if query contains digits)
    if (isPhoneSearch && customer.phoneNumber) {
      // Normalize phone numbers for comparison (remove formatting)
      const normalizedPhone = customer.phoneNumber.replace(/\D/g, '');
      const queryPhone = phoneDigits;
      
      if (normalizedPhone.includes(queryPhone) || queryPhone.includes(normalizedPhone)) {
        // Exact match gets higher score
        const phoneMatch = normalizedPhone === queryPhone ? 1.0 : 0.8;
        score += phoneMatch * 2.5;
        hasMatch = true;
      }
    }
    
    if (hasMatch) {
      // Normalize score (divide by max possible weight)
      // Max weight is approximately 4+3+3+3.5+2.5 = 16
      const normalizedScore = score / 16;
      results.push({ customer, score: normalizedScore });
    }
  }
  
  // Sort by score (highest first)
  results.sort((a, b) => b.score - a.score);
  
  return results.map(r => r.customer);
}

/**
 * Convenience function for searching businesses
 * Optimized for business search with name, description, address, phone, and service names
 */
export function searchBusinesses<T extends { 
  name: string; 
  description?: string; 
  address?: string; 
  phone?: string;
  services?: Array<{ name: string; description?: string }>;
}>(businesses: T[], query: string): T[] {
  if (!query || query.trim().length === 0) {
    return businesses;
  }
  
  const normalizedQuery = normalizeText(query);
  
  // Try to parse as a number for phone search
  const phoneMatch = normalizedQuery.match(/[\d-()]+/);
  const searchPhone = phoneMatch ? phoneMatch[0].replace(/[^\d]/g, '') : null;
  
  const results: Array<{ business: T; score: number }> = [];
  
  for (const business of businesses) {
    let score = 0;
    let hasMatch = false;
    
    // Search business name (highest weight)
    const nameScore = fuzzyMatch(query, business.name);
    if (nameScore !== null) {
      score += nameScore * 4;
      hasMatch = true;
    }
    
    // Search description (medium weight)
    if (business.description) {
      const descScore = fuzzyMatch(query, business.description);
      if (descScore !== null) {
        score += descScore * 2.5;
        hasMatch = true;
      }
    }
    
    // Search address (medium weight) - extract city if available
    if (business.address) {
      const addressScore = fuzzyMatch(query, business.address);
      if (addressScore !== null) {
        score += addressScore * 2.5;
        hasMatch = true;
      }
      
      // Also try to extract city name from address (common pattern: "City, State" or "City State")
      const cityMatch = business.address.match(/^([^,]+)/);
      if (cityMatch) {
        const cityScore = fuzzyMatch(query, cityMatch[1].trim());
        if (cityScore !== null) {
          score += cityScore * 2;
          hasMatch = true;
        }
      }
    }
    
    // Search phone (if query contains numbers)
    if (business.phone && searchPhone) {
      const phoneDigits = business.phone.replace(/[^\d]/g, '');
      if (phoneDigits.includes(searchPhone) || searchPhone.includes(phoneDigits)) {
        score += 2;
        hasMatch = true;
      }
    } else if (business.phone) {
      // Also do fuzzy match on phone as text
      const phoneScore = fuzzyMatch(query, business.phone);
      if (phoneScore !== null) {
        score += phoneScore * 1.5;
        hasMatch = true;
      }
    }
    
    // Search service names if available (high weight for service matches)
    if (business.services && Array.isArray(business.services)) {
      for (const service of business.services) {
        const serviceNameScore = fuzzyMatch(query, service.name);
        if (serviceNameScore !== null) {
          score += serviceNameScore * 3.5; // High weight for service name matches
          hasMatch = true;
        }
        
        // Also search service description
        if (service.description) {
          const serviceDescScore = fuzzyMatch(query, service.description);
          if (serviceDescScore !== null) {
            score += serviceDescScore * 2;
            hasMatch = true;
          }
        }
      }
    }
    
    if (hasMatch) {
      // Normalize score (divide by max possible weight)
      // Max weight is approximately 4+2.5+2.5+2+2+3.5+2 = 18.5 (but can be higher with multiple services)
      const normalizedScore = score / 20; // Use 20 as a reasonable max
      results.push({ business, score: normalizedScore });
    }
  }
  
  // Sort by score (highest first)
  results.sort((a, b) => b.score - a.score);
  
  return results.map(r => r.business);
}

